import React, { useState, useEffect, useContext } from "react";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { SessionContext } from "../../utility/useSession";
import { api, type Brand, type IUpdateBrand } from "@/logic/api";
import { AddEditBrandModal } from "./AddEditBrandModal";
import { BrandListItem } from "./BrandListItem";

export const BrandManagementCard: React.FC = () => {
    const { t } = useTranslation();
    const session = useContext(SessionContext);
    const [brands, setBrands] = useState<Brand[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>("");
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingBrand, setEditingBrand] = useState<Brand | undefined>();
    const [deletingBrand, setDeletingBrand] = useState<Brand | undefined>();
    const [isSaving, setIsSaving] = useState(false);
    const [settingDefaultId, setSettingDefaultId] = useState<number | null>(null);

    const listBrandsHook = api.hooks.brand.listBrands();
    const createBrandHook = api.hooks.brand.createBrand();
    const updateBrandHook = api.hooks.brand.updateBrand();
    const deleteBrandHook = api.hooks.brand.deleteBrand();


    const loadBrands = async () => {
        setLoading(true);
        setError("");

        return new Promise<void>((resolve) => {
            listBrandsHook.send(
                session.current.context,
                { includeInactive: false },
                (result) => {
                    if (result) {
                        setBrands(result);
                    } else if (listBrandsHook.err) {
                        console.error("Error loading brands:", listBrandsHook.err);
                        setError(t("brand.errors.loadFailed"));
                    }
                    setLoading(false);
                    resolve();
                }
            );
        });
    };


    useEffect(() => {
        const fetchData = async () => {
            await loadBrands();
        };
        fetchData();
    }, []);

    const uploadBrandLogo = async (brandCode: string, logoFile: File): Promise<Brand> => {
        const formData = new FormData();
        formData.append("file", logoFile);

        const headers: Record<string, string> = {
            "filename": logoFile.name,
            "authorization": session.current.context.headers.authorization
        };

        const response = await fetch(`${session.current.context.url.replace("/api", "")}/brand/image/upload/${brandCode}`, {
            method: 'POST',
            headers,
            body: formData
        });

        if (!response.ok) {
            throw new Error(`Upload failed with status ${response.status}`);
        }

        const brand = await response.json();
        return brand;
    };

    const handleAddBrand = async (brandData: Omit<Brand, "id">, logoFile?: File) => {
        try {
            setIsSaving(true);

            // Create brand
            const newBrand = await new Promise<Brand>((resolve, reject) => {
                createBrandHook.send(
                    session.current.context,
                    {
                        brandCode: brandData.brandCode,
                        displayName: brandData.displayName,
                        default: brandData.default,
                        active: brandData.active ?? true,
                        fileName: null
                    },
                    (result) => {
                        if (result) {
                            resolve(result);
                        } else {
                            reject(createBrandHook.err);
                        }
                    }
                );
            });

            let finalBrand = newBrand;

            // upload logo after brand creation
            if (logoFile) {
                finalBrand = await uploadBrandLogo(newBrand.brandCode, logoFile);
            }

            setBrands((prev) => {
                const without = prev.filter(
                    (brand) => brand.id !== finalBrand.id && brand.brandCode !== finalBrand.brandCode
                );
                return [...without, finalBrand];
            });
            setShowAddModal(false);
            await loadBrands();
        } catch (err) {
            console.error("Error creating brand:", err);
            throw new Error(t("brand.errors.createFailed"));
        } finally {
            setIsSaving(false);
        }
    };

    const handleEditBrand = async (brandData: Omit<Brand, "id">, logoFile?: File) => {
        if (!editingBrand?.id) return;

        try {
            setIsSaving(true);

            // upload new logo first if provided
            if (logoFile) {
                await uploadBrandLogo(brandData.brandCode, logoFile);
            }

            // Build update params
            const updateParams: IUpdateBrand = {
                brandCode: editingBrand.brandCode, // current code for lookup
                newBrandCode: editingBrand.brandCode !== brandData.brandCode ? brandData.brandCode : undefined,
                displayName: brandData.displayName,
                default: brandData.default,
                active: brandData.active ?? true
            };

            // only include fileName if we're explicitly removing it
            if (!logoFile && brandData.fileName === "") {
                updateParams.fileName = null;
            }

            // update brand properties
            const updatedBrand = await new Promise<Brand>((resolve, reject) => {
                updateBrandHook.send(
                    session.current.context,
                    updateParams,
                    (result) => {
                        if (result) {
                            resolve(result);
                        } else {
                            reject(updateBrandHook.err);
                        }
                    }
                );
            });

            setBrands(prev => prev.map(b => b.id === editingBrand.id ? updatedBrand : b));
            setEditingBrand(undefined);
            await loadBrands();
        } catch (err) {
            console.error("Error updating brand:", err);
            throw new Error(t("brand.errors.updateFailed"));
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteBrand = async () => {
        if (!deletingBrand?.brandCode) return;

        try {
            setIsSaving(true);

            await new Promise<void>((resolve, reject) => {
                deleteBrandHook.send(
                    session.current.context,
                    { brandCode: deletingBrand.brandCode },
                    (result) => {
                        if (result !== undefined) {
                            resolve();
                        } else {
                            reject(deleteBrandHook.err);
                        }
                    }
                );
            });

            setBrands(prev => prev.filter(b => b.id !== deletingBrand.id));
            setDeletingBrand(undefined);
            await loadBrands();
        } catch (err) {
            console.error("Error deleting brand:", err);
            setError(t("brand.errors.deleteFailed"));
        } finally {
            setIsSaving(false);
        }
    };

    const handleSetDefault = async (brand: Brand) => {
        if (!brand.id || !brand.brandCode) return;

        try {
            setSettingDefaultId(brand.id);
            const isCurrentlyDefault = brand.default;

            await new Promise<void>((resolve, reject) => {
                updateBrandHook.send(
                    session.current.context,
                    {
                        brandCode: brand.brandCode,
                        displayName: brand.displayName,
                        default: !isCurrentlyDefault, // we need this to toggle the default status
                        active: brand.active ?? true
                    },
                    (result) => {
                        if (result) {
                            resolve();
                        } else {
                            reject(updateBrandHook.err);
                        }
                    }
                );
            });

            if (isCurrentlyDefault) {
                // If clicking on current default, remove it as default (allow no default)
                setBrands(prev => prev.map(b => ({ ...b, default: false })));
            } else {
                // If clicking on non-default, make it the only default
                setBrands(prev => prev.map(b => ({ ...b, default: b.id === brand.id })));
            }
        } catch (err) {
            console.error("Error updating default brand:", err);
            setError(t("brand.errors.setDefaultFailed"));
        } finally {
            setSettingDefaultId(null);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box data-testid="brand-management-card">
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0.5 }}>
                        {t("brand.management.title")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t("brand.management.description")}
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setShowAddModal(true)}
                    disabled={isSaving}
                    sx={{
                        fontWeight: "bold",
                        backgroundColor: (theme) => theme.palette.brand.main,
                        color: (theme) => theme.palette.primary.contrastText,
                        transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1), box-shadow 150ms ease",
                        "&:hover": {
                            backgroundColor: (theme) => theme.palette.brand.dark,
                            color: (theme) => theme.palette.primary.contrastText,
                        },
                        "&:active": {
                            transform: "scale(0.97)",
                        },
                        "&:focus-visible": {
                            boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}40`,
                        },
                    }}
                    disableRipple
                    disableElevation
                    data-testid="add-brand-button"
                >
                    {t("brand.management.addBrand")}
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
                    {error}
                </Alert>
            )}

            {brands.length === 0 ? (
                <Box
                    sx={{
                        textAlign: "center",
                        py: 6,
                        border: "1px dashed #e0e0e0",
                        borderRadius: 1,
                        bgcolor: "#f9f9f9"
                    }}
                >
                    <Typography variant="body1" color="text.secondary">
                        {t("brand.management.noBrands")}
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {brands.map((brand) => (
                        <BrandListItem
                            key={brand.id}
                            brand={brand}
                            isDefault={brand.default || false}
                            onEdit={setEditingBrand}
                            onDelete={setDeletingBrand}
                            onSetDefault={handleSetDefault}
                            disabled={isSaving}
                            isSettingDefault={settingDefaultId === brand.id}
                        />
                    ))}
                </Box>
            )}

            <AddEditBrandModal
                open={showAddModal || Boolean(editingBrand)}
                onClose={() => {
                    setShowAddModal(false);
                    setEditingBrand(undefined);
                }}
                onSave={editingBrand ? handleEditBrand : handleAddBrand}
                brand={editingBrand}
                isLoading={isSaving}
            />

            <Dialog
                open={Boolean(deletingBrand)}
                onClose={() => setDeletingBrand(undefined)}
                maxWidth="sm"
                fullWidth
                data-testid="delete-brand-confirmation"
            >
                <DialogTitle>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {t("brand.actions.confirmDelete")}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        {t("brand.deleteDialog.confirmMessage", { displayName: deletingBrand?.displayName })}
                        {deletingBrand?.default && (
                            <>{t("brand.deleteDialog.defaultBrandWarning")}</>
                        )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {t("brand.deleteDialog.cannotUndo")}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={() => setDeletingBrand(undefined)}
                        disabled={isSaving}
                        variant="text"
                        sx={{
                            transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1), box-shadow 150ms ease",
                            "&:active": {
                                transform: "scale(0.97)",
                            },
                            "&:hover": {
                                backgroundColor: (theme) => theme.palette.action.hover,
                            },
                            "&:focus-visible": {
                                boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}40`,
                            },
                        }}
                        disableRipple
                        disableElevation
                    >
                        {t("brand.actions.cancel")}
                    </Button>
                    <Button
                        onClick={handleDeleteBrand}
                        variant="contained"
                        color="error"
                        disabled={isSaving}
                        sx={{
                            fontWeight: "bold",
                            transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1), box-shadow 150ms ease",
                            "&:active": {
                                transform: "scale(0.97)",
                            },
                            "&:focus-visible": {
                                boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}40`,
                            },
                        }}
                        disableRipple
                        disableElevation
                        data-testid="confirm-delete-brand"
                    >
                        {isSaving ? t("brand.actions.deleting") : t("brand.actions.deleteBrandButton")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};